import { useEffect, useRef } from 'react';
import type { NodeBounds } from '@/layout/types';
import { useEditorStore } from '@/store/editorStore';
import { useDocumentStore } from '@/store/documentStore';
import { commandRegistry } from '@/commands/registry';
import type { CommandContext } from '@/commands/types';

interface Props {
  nodeId: string;
  bounds: NodeBounds;
}

export function InlineEditor({ nodeId, bounds }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const stopEditing = useEditorStore((s) => s.stopEditing);
  const isNewNode = useEditorStore((s) => s.isNewNode);
  const setIsNewNode = useEditorStore((s) => s.setIsNewNode);
  const mapDoc = useDocumentStore((s) => s.mapDoc);
  const node = useDocumentStore((s) => s.nodes.get(nodeId));

  useEffect(() => {
    const input = inputRef.current;
    if (input) {
      input.focus();
      input.select();
    }
  }, []);

  /** Commit text if non-empty, or delete the node if it has no content at all. */
  function commitOrDiscard(value: string): void {
    const trimmed = value.trim();
    const currentNode = mapDoc.getNode(nodeId);

    const hasContent = trimmed !== ''
      || (currentNode?.notes && currentNode.notes.trim() !== '')
      || (currentNode?.links && currentNode.links.length > 0)
      || (currentNode?.attachments && currentNode.attachments.length > 0);

    if (hasContent) {
      mapDoc.updateNodeText(nodeId, trimmed || value);
    } else {
      // Empty node with no content -- discard it (unless it's the root)
      const meta = mapDoc.getMeta();
      if (nodeId !== meta.rootNodeId && currentNode?.parentId) {
        // Select parent before deleting
        useEditorStore.getState().selectNode(currentNode.parentId);
        mapDoc.deleteNode(nodeId);
      }
    }
  }

  /** Cancel a newly created node: delete it and select its parent. */
  function cancelNewNode(): void {
    const currentNode = mapDoc.getNode(nodeId);
    const meta = mapDoc.getMeta();
    if (nodeId !== meta.rootNodeId && currentNode?.parentId) {
      useEditorStore.getState().selectNode(currentNode.parentId);
      mapDoc.deleteNode(nodeId);
    }
    stopEditing();
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Enter is an intentional confirmation -- always commit, even for new nodes
      setIsNewNode(false);
      commitOrDiscard(inputRef.current?.value ?? '');
      stopEditing();
      const ctx: CommandContext = { doc: mapDoc, editorState: useEditorStore.getState() };
      commandRegistry.execute('node.addSiblingBelow', ctx);
    } else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      // Shift+Tab adds a child at the top of the child list
      setIsNewNode(false);
      commitOrDiscard(inputRef.current?.value ?? '');
      stopEditing();
      const ctx: CommandContext = { doc: mapDoc, editorState: useEditorStore.getState() };
      commandRegistry.execute('node.addChildAbove', ctx);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      // Tab is an intentional confirmation -- always commit, even for new nodes
      setIsNewNode(false);
      commitOrDiscard(inputRef.current?.value ?? '');
      stopEditing();
      const ctx: CommandContext = { doc: mapDoc, editorState: useEditorStore.getState() };
      commandRegistry.execute('node.addChild', ctx);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (isNewNode) {
        cancelNewNode();
      } else {
        commitOrDiscard(inputRef.current?.value ?? '');
        stopEditing();
      }
    }
    e.stopPropagation();
  };

  const handleBlur = () => {
    if (isNewNode) {
      cancelNewNode();
    } else {
      commitOrDiscard(inputRef.current?.value ?? '');
      stopEditing();
    }
  };

  return (
    <foreignObject
      x={bounds.x}
      y={bounds.y}
      width={Math.max(bounds.width, 120)}
      height={bounds.height}
    >
      <input
        ref={inputRef}
        type="text"
        defaultValue={node?.text ?? ''}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        style={{
          width: '100%',
          height: '100%',
          border: '2px solid #f59e0b',
          borderRadius: '6px',
          background: '#1e293b',
          color: node?.style?.textColor ?? '#f8fafc',
          fontSize: node?.style?.fontSize ? `${node.style.fontSize}px` : '14px',
          fontWeight: node?.style?.fontWeight ?? 'normal',
          fontStyle: node?.style?.fontStyle ?? 'normal',
          textDecoration: node?.style?.textDecoration ?? 'none',
          padding: '0 8px',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
    </foreignObject>
  );
}
